import { NgModule } from "@angular/core";
import { RouterModule, Routes } from "@angular/router";
import {
  AuthenticationCanActivateGuard,
  AuthenticationCanMatchGuard,
  RedirectGuard,
  UserProfileGuard
} from "@vp/shared/guards";
import { environment } from "../environments/environment";
import { ErrorComponent } from "./pages/error/error.component";
import { UninvitedComponent } from "./pages/uninvited/uninvited.component";

const routes: Routes = [
  {
    path: "login",
    data: {
      breadcrumb: "homeNav"
    },
    loadChildren: () => import("./pages/login/login.module").then(m => m.LoginModule)
  },
  { path: "error", component: ErrorComponent },
  { path: "uninvited", component: UninvitedComponent },
  { path: "", redirectTo: "/home", pathMatch: "full" },
  { path: "loggedout", redirectTo: "/login", pathMatch: "full" },
  {
    path: "forbidden",
    loadChildren: () => import("./pages/forbidden/forbidden.module").then(m => m.ForbiddenModule)
  },
  {
    path: "unauthorized",
    loadChildren: () =>
      import("./pages/unauthorized/unauthorized.module").then(m => m.UnauthorizedModule)
  },
  {
    path: "wizard",
    data: {
      breadcrumb: "caseNav"
    },
    loadChildren: () => import("@vp/case-create/feature").then(m => m.CaseCreateFeatureModule)
  },

  // BEGIN authenticated only routes
  {
    path: "login/callback",
    pathMatch: "full",
    canActivate: [AuthenticationCanActivateGuard, RedirectGuard],
    children: []
  },
  {
    path: "loggedin",
    pathMatch: "full",
    canActivate: [AuthenticationCanActivateGuard, RedirectGuard],
    children: []
  },
  {
    path: "default",
    pathMatch: "full",
    canActivate: [AuthenticationCanActivateGuard, RedirectGuard],
    children: []
  },
  {
    path: "home",
    canMatch: [AuthenticationCanMatchGuard],
    data: { hideSearch: true },
    loadChildren: () => import("../app/pages/home/home.module").then(m => m.HomeModule)
  },
  {
    path: "profile",
    canMatch: [AuthenticationCanMatchGuard],
    data: {
      breadcrumb: "profile"
    },
    loadChildren: () => import("@vp/user-profile/feature").then(m => m.UserProfileFeatureModule)
  },
  {
    path: "case",
    canMatch: [AuthenticationCanMatchGuard],
    canActivate: [UserProfileGuard],
    loadChildren: () => import("@vp/case-dashboard/feature").then(m => m.CaseDashboardFeatureModule)
  },
  {
    path: "cases",
    canMatch: [AuthenticationCanMatchGuard],
    canActivate: [UserProfileGuard],
    runGuardsAndResolvers: "always",
    loadChildren: () => import("@vp/case-request/feature").then(m => m.CaseRequestFeatureModule)
  },
  {
    path: "vcare",
    canMatch: [AuthenticationCanMatchGuard],
    canActivate: [UserProfileGuard],
    runGuardsAndResolvers: "always",
    loadChildren: () => import("@vp/v-care/feature").then(m => m.VCareFeatureModule)
  },
  {
    path: "devices",
    canMatch: [AuthenticationCanMatchGuard],
    canActivate: [UserProfileGuard],
    runGuardsAndResolvers: "always",
    loadChildren: () => import("@vp/direct-connect/feature").then(m => m.DirectConnectFeatureModule)
  },
  {
    path: "patients",
    canMatch: [AuthenticationCanMatchGuard],
    canActivate: [UserProfileGuard],
    runGuardsAndResolvers: "always",
    loadChildren: () =>
      import("@vp/patient-manager/feature").then(m => m.PatientManagerFeatureModule)
  },
  {
    path: "queue",
    canMatch: [AuthenticationCanMatchGuard],
    canActivate: [UserProfileGuard],
    runGuardsAndResolvers: "always",
    loadChildren: () => import("@vp/case-queue/feature").then(m => m.CaseQueueFeatureModule)
  },
  {
    path: "administration",
    canMatch: [AuthenticationCanMatchGuard],
    canActivate: [UserProfileGuard],
    runGuardsAndResolvers: "always",
    loadChildren: () => import("@vp/administration/feature").then(m => m.AdministrationModule)
  },
  {
    path: "tagmanager",
    canMatch: [AuthenticationCanMatchGuard],
    canActivate: [UserProfileGuard],
    runGuardsAndResolvers: "always",
    loadChildren: () => import("@vp/tag-manager/feature").then(m => m.TagManagerFeatureModule)
  },
  {
    path: "about",
    outlet: "sidebar",
    canMatch: [AuthenticationCanMatchGuard],
    canActivate: [UserProfileGuard],
    runGuardsAndResolvers: "always",
    loadChildren: () => import("./pages/about/about.module").then(m => m.AboutModule)
  },
  {
    path: "content",
    canMatch: [AuthenticationCanMatchGuard],
    canActivate: [UserProfileGuard],
    runGuardsAndResolvers: "always",
    loadChildren: () =>
      import("@vp/content-management/feature").then(m => m.ContentManagementFeatureModule)
  },
  {
    path: "system-information",
    canMatch: [AuthenticationCanMatchGuard],
    canActivate: [UserProfileGuard],
    runGuardsAndResolvers: "always",
    loadChildren: () =>
      import("@vp/system-information/feature").then(m => m.SystemInformationFeatureModule)
  },
  {
    path: "vcompanion",
    canMatch: [AuthenticationCanMatchGuard],
    canActivate: [UserProfileGuard],
    runGuardsAndResolvers: "always",
    loadChildren: () => import("@vp/v-companion/feature").then(m => m.VCompanionFeatureModule)
  },
  {
    path: "vci",
    canMatch: [AuthenticationCanMatchGuard],
    canActivate: [UserProfileGuard],
    runGuardsAndResolvers: "always",
    loadChildren: () => import("@vp/vci/feature").then(m => m.VCIFeatureModule)
  },
  {
    path: "clinical-informatics-support",
    loadChildren: () =>
      import("@vp/vci/ui/clinical-informatics-support").then(
        m => m.ClinicalInformaticsSupportModule
      )
  },
  {
    // Dev-only: live reference for the shared design-system primitives. The
    // `canMatch` guard makes the route unreachable in production builds
    // (the chunk is lazy, so it's never loaded there).
    path: "design-gallery",
    canMatch: [() => !environment.production],
    loadComponent: () => import("@vp/shared/ui/gallery").then(m => m.VpDesignGalleryComponent)
  },
  { path: "**", redirectTo: "/default", pathMatch: "full" }
];

export const enableTracing = false; // <-- Set to true to debug router in dev mode

@NgModule({
  imports: [
    RouterModule.forRoot(routes, {
      enableTracing: environment.production ? false : enableTracing,
      useHash: false,
      onSameUrlNavigation: "reload",
      initialNavigation: "disabled"
    })
  ],
  exports: [RouterModule]
})
export class AppRoutingModule {}

export function isInIframe() {
  return window !== window.parent && !window.opener;
}
